const normalized = value => String(value == null ? '' : value).trim().toLowerCase();

const includesSearch = (record, fields, search) => {
  const needle = normalized(search);
  return !needle || fields.some(field => normalized(record[field]).includes(needle));
};

export const parsePaymentWorkspaceLocation = hash => {
  const query = String(hash || '').split('?')[1] || '';
  const params = new URLSearchParams(query);
  return {
    tab: params.get('view') === 'history' ? 'history' : 'collection',
    commitmentId: params.get('commitmentId') || null,
    paymentId: params.get('paymentId') || null,
  };
};

export const filterCollectionAgreements = (records, filters = {}) => (records || []).filter(record => {
  const queue = filters.queue || 'ALL';
  if (queue !== 'ALL' && (record.status === 'CANCELLED' || record.collectionStatus === 'CANCELLED')) return false;
  const inQueue = queue === 'ALL'
    || (queue === 'OUTSTANDING' && Number(record.outstandingMinor || 0) > 0)
    || (queue === 'OVERDUE' && Number(record.overdueMinor || 0) > 0)
    || (queue === 'DUE_SOON' && Number(record.dueSoonMinor || 0) > 0)
    || (queue === 'PAID' && ['PAID_ON_TIME', 'PAID_LATE', 'PAID_UNKNOWN'].includes(record.collectionStatus))
    || record.collectionStatus === queue;
  return inQueue && includesSearch(record, ['customerName', 'agreementNumber', 'reference', 'title', 'kind'], filters.search);
});

export const filterReceiptHistory = (records, filters = {}) => (records || []).filter(record => (
  (filters.status === 'ALL' || !filters.status || record.status === filters.status)
  && includesSearch(record, ['customerName', 'agreementNumber', 'reference', 'method', 'recordedByName'], filters.search)
));

export const sortPaymentRecords = (records, field, direction = 'asc') => (records || [])
  .map((record, index) => ({ record, index }))
  .sort((left, right) => {
    const a = left.record[field];
    const b = right.record[field];
    const aMissing = a == null || a === '';
    const bMissing = b == null || b === '';
    if (aMissing !== bMissing) return aMissing ? 1 : -1;
    if (aMissing) return left.index - right.index;
    const comparison = typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    return comparison === 0 ? left.index - right.index : comparison * (direction === 'desc' ? -1 : 1);
  })
  .map(entry => entry.record);

export const voidPaymentState = receipt => {
  if (receipt?.status === 'VOIDED') return { allowed: false, reason: 'This receipt has already been voided.' };
  return { allowed: true, reason: null };
};

export const receiptRecordState = agreement => {
  if (!agreement) return { allowed: false, reason: 'Select an agreement first.' };
  if (agreement.status === 'CANCELLED' || agreement.collectionStatus === 'CANCELLED') return {
    allowed: false, reason: 'Cancelled agreements cannot receive new receipts.',
  };
  if (Number(agreement.outstandingMinor || 0) <= 0) return { allowed: false, reason: 'This agreement is fully collected.' };
  return { allowed: true, reason: null };
};

export const schedulePlanningState = agreement => {
  const activeInstallments = (agreement?.installments || []).filter(stage => stage.status !== 'CANCELLED');
  const scheduledMinor = activeInstallments.reduce((sum, stage) => sum + Number(stage.amountMinor || 0), 0);
  const unscheduledMinor = Math.max(0, Number(agreement?.totalAmountMinor || 0) - scheduledMinor);
  const cancelled = agreement?.status === 'CANCELLED' || agreement?.collectionStatus === 'CANCELLED';
  return {
    activeInstallments,
    scheduledMinor,
    unscheduledMinor,
    automaticScheduleOnReceipt: activeInstallments.length === 0,
    canAdd: !cancelled && unscheduledMinor > 0,
    addReason: cancelled
      ? 'Cancelled agreements cannot change their payment schedule.'
      : unscheduledMinor <= 0 ? 'The full agreement value is already assigned to payment stages.' : null,
  };
};

export const installmentPlanningState = (agreement, installment) => {
  const schedule = schedulePlanningState(agreement);
  const maximumMinor = Number(installment?.amountMinor || 0) + schedule.unscheduledMinor;
  const hasHistoricalAllocation = (agreement?.payments || []).some(payment => (
    (payment.allocations || []).some(allocation => String(allocation.installmentId) === String(installment?.id))
  ));
  if (agreement?.status === 'CANCELLED' || agreement?.collectionStatus === 'CANCELLED') return {
    editable: false,
    reason: 'Cancelled agreements cannot change their payment schedule.',
    maximumMinor,
  };
  if (Number(installment?.paidAmountMinor || 0) > 0 || hasHistoricalAllocation) return {
    editable: false,
    reason: 'Locked after a receipt was allocated. Its due date and amount are preserved for audit accuracy.',
    maximumMinor,
  };
  return { editable: true, reason: null, maximumMinor };
};

export const paymentStatusLabel = status => ({
  OVERDUE: 'Overdue',
  DUE_SOON: 'Due soon',
  OUTSTANDING: 'Outstanding',
  PAID: 'Paid',
  PAID_ON_TIME: 'Paid · on time',
  PAID_LATE: 'Paid · late',
  PAID_UNKNOWN: 'Paid · date unknown',
  POSTED: 'Recorded',
  VOIDED: 'Voided',
}[status] || String(status || 'Unknown').replace(/_/g, ' ').toLowerCase().replace(/^./, letter => letter.toUpperCase()));
